import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Form, Row, Col, Button, Card, Image} from 'react-bootstrap';
import ProfileMobileSectionActions from './ProfileMobileSectionActions';
import icValidationError from './assets/img/ic-danger.svg';
import icGlobe from './assets/img/ic-globe.svg';
import icCity from './assets/img/ic-city.svg';
import icLanguages from './assets/img/ic-languages.svg';
import europeanCountries from './data/europeanCountries.js';
import universityCities from './data/universityCities.js';
import ResponsiveMultiSelect from './components/Profile/ResponsiveMultiSelect';

const UserAddRegion = forwardRef(({ formData, handleChange, errors, isEditPage, handleRegionSubmit, hideEdit }, ref) => {
    const [isEditing, setIsEditing] = useState(!isEditPage); // If adding, fields start enabled
    const [tempFormData, setTempFormData] = useState({ ...formData }); // Store initial values
    const [localErrors, setLocalErrors] = useState({});

    useEffect(() => {
        setTempFormData({ ...formData });
    }, [formData]);

    const countryOptions = europeanCountries;

    const validate = () => {
        let isValid = true;
        const newLocalErrors = {};

        if (!formData.countriesWorkPreferences?.length) {
            newLocalErrors.countriesWorkPreferences = 'At least one country is required.';
            isValid = false;
        }

        if (!formData.citiesWorkPreferences?.length) {
            newLocalErrors.citiesWorkPreferences = 'At least one city is required.';
            isValid = false;
        }

        if (!formData.languagesWorkPreferences?.length) {
            newLocalErrors.languagesWorkPreferences = 'At least one language is required.';
            isValid = false;
        }

        setLocalErrors(newLocalErrors);
        return isValid;
    };

    useImperativeHandle(ref, () => ({ validate }));


    const showCountriesError = !formData.countriesWorkPreferences?.length &&
        (errors.countriesWorkPreferences || localErrors.countriesWorkPreferences);
    const showCitiesError = !formData.citiesWorkPreferences?.length &&
        (errors.citiesWorkPreferences || localErrors.citiesWorkPreferences);
    const showLanguagesError = !formData.languagesWorkPreferences?.length &&
        (errors.languagesWorkPreferences || localErrors.languagesWorkPreferences);

    const filteredCityOptions = formData.countriesWorkPreferences?.flatMap((code) =>
        (universityCities[code] || []).map((city) => ({
            value: city,
            label: city,
            countryCode: code
        }))
    ) || [];

    const allLanguagesSet = new Set(["English"]); // ✅ always include English

    formData.countriesWorkPreferences?.forEach((code) => {
        const country = countryOptions.find(c => c.value === code);
        country?.languages?.forEach((lang) => allLanguagesSet.add(lang));
    });

    const filteredLanguageOptions = Array.from(allLanguagesSet).map((lang) => ({
        value: lang,
        label: lang
    }));

    const getSelectedLanguageOptions = () => {
        if (!Array.isArray(formData.languagesWorkPreferences)) return [];
        return filteredLanguageOptions.filter((opt) =>
            formData.languagesWorkPreferences.includes(opt.value)
        );
    };

    const getSelectedCityOptions = () => {
        if (!Array.isArray(formData.citiesWorkPreferences)) return [];
        return filteredCityOptions.filter((opt) => formData.citiesWorkPreferences.includes(opt.value));
    };

    const handleCountriesChange = (selected) => {
        const selectedValues = selected ? selected.map((opt) => opt.value) : [];

        if (selectedValues.length === 0) {
            setLocalErrors((prev) => ({ ...prev, countriesWorkPreferences: 'At least one country is required.' }));
        } else {
            setLocalErrors((prev) => {
                const updated = { ...prev };
                delete updated.countriesWorkPreferences;
                return updated;
            });
        }

        handleChange({ target: { name: 'countriesWorkPreferences', value: selectedValues } });
    };

    const handleCityChange = (selected) => {
        const selectedValues = selected ? selected.map((opt) => opt.value) : [];

        if (selectedValues.length === 0) {
            setLocalErrors((prev) => ({ ...prev, citiesWorkPreferences: 'At least one city is required.' }));
        } else {
            setLocalErrors((prev) => {
                const updated = { ...prev };
                delete updated.citiesWorkPreferences;
                return updated;
            });
        }

        handleChange({ target: { name: 'citiesWorkPreferences', value: selectedValues } });
    };

    const handleLanguagesChange = (selected) => {
        const selectedValues = selected ? selected.map((opt) => opt.value) : [];

        if (selectedValues.length === 0) {
            setLocalErrors((prev) => ({ ...prev, languagesWorkPreferences: 'At least one language is required.' }));
        } else {
            setLocalErrors((prev) => {
                const updated = { ...prev };
                delete updated.languagesWorkPreferences;
                return updated;
            });
        }

        handleChange({ target: { name: 'languagesWorkPreferences', value: selectedValues } });
    };

    // Enable editing mode
    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // Store previous values
    };

    // ✅ Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        if (typeof handleRegionSubmit === 'function') {
            await handleRegionSubmit(e);
            setIsEditing(false);
        } else {
            console.error('Error: handleRegionSubmit is not a function.');
        }
    };

    // Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false); // Exit edit mode

        console.log('Restoring previous values:', tempFormData);
    };

    return (
        <>
            <Card className="mb-4">
                <Card.Body>
                    {/* Title and Edit Button */}
                    <Row>
                        <Col>
                            <div className="profile-section-heading-row">
                            {isEditPage && !hideEdit && (
                                <div className="float-end d-none d-lg-block">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3 className="company-detail-section-title d-lg-none"><span className="item-number">1.</span> Region preferences</h3>
                            <h3 className="d-none d-lg-flex"><span className="item-number">1.</span> Region preferences</h3>
                            </div>
                            <hr />
                        </Col>
                    </Row>
                    <>
                        {isEditing ? (
                            <>      
                                <Row className="mb-3">
                                    {/* Form Fields */}
                                    <Col md={12}>
                                        <Row className="mb-3 profile-edit-fields">
                                            <Col md={4}>
                                                <Form.Group className="validated" controlId="countriesWorkPreferences">
                                                    <Form.Label>Countries <span>*</span></Form.Label>
                                                    <ResponsiveMultiSelect
                                                        label="Countries"
                                                        inputId="countriesWorkPreferences"
                                                        placeholder="Select countries"
                                                        className={`react-select ${showCountriesError ? 'is-invalid' : ''}`}
                                                        classNamePrefix="react-select"
                                                        isDisabled={!isEditing}
                                                        value={countryOptions.filter((c) => (formData.countriesWorkPreferences ?? []).includes(c.value))}
                                                        onChange={handleCountriesChange}
                                                        options={countryOptions}
                                                    />
                                                    {showCountriesError && (
                                                        <div className="custom-error-icon-wrapper">
                                                            <img
                                                                src={icValidationError}
                                                                alt={showCountriesError}
                                                                className="custom-error-icon"
                                                            />
                                                            <div className="custom-tooltip">
                                                                {errors.countriesWorkPreferences || localErrors.countriesWorkPreferences}
                                                            </div>
                                                        </div>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="validated" controlId="citiesWorkPreferences">
                                                    <Form.Label>City <span>*</span></Form.Label>
                                                    <ResponsiveMultiSelect
                                                        label="City"
                                                        inputId="citiesWorkPreferences"
                                                        placeholder="Select cities"
                                                        className={`react-select ${showCitiesError ? 'is-invalid' : ''}`}
                                                        classNamePrefix="react-select"
                                                        isDisabled={!isEditing || !formData.countriesWorkPreferences?.length}
                                                        value={getSelectedCityOptions()}
                                                        onChange={handleCityChange}
                                                        options={filteredCityOptions}
                                                    />
                                                    {showCitiesError && (
                                                        <div className="custom-error-icon-wrapper">
                                                            <img
                                                                src={icValidationError}
                                                                alt={showCitiesError}
                                                                className="custom-error-icon"
                                                            />
                                                            <div className="custom-tooltip">
                                                                {errors.citiesWorkPreferences || localErrors.citiesWorkPreferences}
                                                            </div>
                                                        </div>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="validated" controlId="languagesWorkPreferences">
                                                    <Form.Label>Languages <span>*</span></Form.Label>
                                                    <ResponsiveMultiSelect
                                                        label="Languages"
                                                        inputId="languagesWorkPreferences"
                                                        placeholder="Select languages"
                                                        className={`react-select ${showLanguagesError ? 'is-invalid' : ''}`}
                                                        classNamePrefix="react-select"
                                                        isDisabled={!isEditing || !formData.countriesWorkPreferences?.length}
                                                        value={getSelectedLanguageOptions()}
                                                        onChange={handleLanguagesChange}
                                                        options={filteredLanguageOptions}
                                                    />
                                                    {showLanguagesError && (
                                                        <div className="custom-error-icon-wrapper">
                                                            <img
                                                                src={icValidationError}
                                                                alt={showLanguagesError}
                                                                className="custom-error-icon"
                                                            />
                                                            <div className="custom-tooltip">
                                                                {errors.languagesWorkPreferences || localErrors.languagesWorkPreferences}
                                                            </div>
                                                        </div>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </>
                        ): (
                            // Not editing
                            <>
                                <Row className="mb-4">
                                    <Col md={3}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icGlobe} alt="Countries" />
                                            </span>
                                            <span className="text">
                                                Countries
                                                <br />
                                                <strong>
                                                    {(countryOptions
                                                        .filter(opt => (formData.countriesWorkPreferences ?? []).includes(opt.value))
                                                        .map(opt => opt.label)
                                                        .join(', ')
                                                        ) || "N/A"
                                                    }
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icCity} alt="Cities" />
                                            </span>
                                            <span className="text">
                                                Cities
                                                <br />
                                                <strong>
                                                    {(filteredCityOptions
                                                        .filter(opt => (formData.citiesWorkPreferences ?? []).includes(opt.value))
                                                        .map(opt => opt.label)                                                    
                                                        .join(', ') || 'N/A'                                                       
                                                    )}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                    
                                    <Col md={4}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icLanguages} alt="Languages" />
                                            </span>
                                            <span className="text">
                                                Languages
                                                <br />
                                                <strong>
                                                    {(filteredLanguageOptions
                                                        .filter(opt => (formData.languagesWorkPreferences ?? []).includes(opt.value))
                                                        .map(opt => opt.label)                                                    
                                                        .join(', ') || 'N/A'                                                       
                                                    )}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </>
                    <ProfileMobileSectionActions
                        isEditing={isEditing}
                        onEdit={handleEditClick}
                        onCancel={handleCancelClick}
                        onSave={handleSaveClick}
                        show={isEditPage && !hideEdit}
                    />
                </Card.Body>
            </Card>
        </>
    );
});

export default UserAddRegion
